import React, { FC, useState, useEffect } from "react";
import { Select, Form, message } from "antd";
import { Prompt, useHistory } from "react-router-dom";
import { Timestamp } from "../../../firebase";

import { data, validation } from "../../../constants";
import { PersonalProfile } from "../../organisms/ApplicantPersonalProfileForm";
import avatar from "../../../assets/images/placeholder.svg";
import { theme } from "../../../config";
import { formProps as SkillsFormProps } from "../../organisms/ApplicantSkillsForm/types";
import { SkillsForm } from "../../organisms/ApplicantSkillsForm";
import { ApplicantContacts } from "../../organisms/ApplicantContactsForm";
import { ContactsFormProps } from "../../organisms/ApplicantContactsForm/types";
import { PersonalFormProps } from "../../organisms/ApplicantPersonalProfileForm/types";
import ApplicantForm from "../../templates/ApplicantForm/ApplicantForm";
import { PreScreeningFormProps } from "../../organisms/ApplicantPreScreeningForm/types";
import { Props, StepsProps } from "./types";
import { ApplicantType } from "../../../core/entities/applicant";

const { Option } = Select;

const ApplicantDetailsStepperPage: FC<Props> = (props) => {
  const {
    image,
    showPrompt,
    positions,
    dispatchSetShowPrompt,
    dispatchUploadFile,
    dispatchAddApplicant,
    error,
    isAddSuccessful,
    clearApplicantValues,
  } = props;

  const history = useHistory();
  const [current, setCurrent] = useState(0);
  // const [buttonLoading, setButtonLoading] = useState(false);
  const [personalForm] = Form.useForm<PersonalFormProps>();
  const [skillsForm] = Form.useForm<SkillsFormProps>();
  const [contactsForm] = Form.useForm<ContactsFormProps>();
  const [preScreeningForm] = Form.useForm<any>();
  const [showUploadResumeError, setShowUploadResumeError] = useState(false);
  const [applicantInfo, setApplicantInfo] = useState<ApplicantType>({
    image: "",
    fullName: "",
    applicantName: {
      firstName: "",
      middleName: "",
      lastName: "",
    },
    positionApplied: "",
    expectedSalary: 0,
    birthDate: Timestamp.now(),
    civilStatus: "",
    degree: "",
    portfolio: "",
    dateCompleted: Timestamp.now(),
    school: "",
    course: "",
    referredBy: "",
    skills: [],
    linkedinUrl: "",
    email: "",
    contactNumber: "",
    contactTimeTo: "",
    contactTimeFrom: "",
    address: "",
    characterReference: [],
    willingToRelocate: "",
    yearsOfExperience: "",
    recentWork: "",
    reasonOfLeaving: "",
    toolsUsed: "",
    agileExperience: "",
    workOffice: "",
    platformApplied: "",
    resume: "",
  });
  const [skillList, setSkillList] = useState([] as Array<string>);
  const [isFreshGrad, setIsFreGrad] = useState(false);

  const beforeUnloadHandler = (e: any) => {
    e.preventDefault();
    return (e.returnValue = "Are you sure you want to close?");
  };

  useEffect(() => {
    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error && isAddSuccessful === false) {
      message.config({ duration: 1.5, maxCount: 1 });
      message.error(error);
    } else if (!error && isAddSuccessful === true) {
      clearApplicantValues();
      history.replace("/careers/success");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isAddSuccessful]);

  const modalTitle = "Authorization";
  const modalBody = (
    <p>
      I certify that the information and its supporting documents are accurate
      and complete. I understand and agree that failure to fully complete the
      form, or misinterpretation or omission of facts, represents grounds for
      elimination from consideration for employment, or termination after
      employment if discovered at a later date. I authorize Everbank Vananaz,
      Inc., top investigate, without liability, all statements contained in this
      application and supporting materials. I authorize references and former
      employers, without liability, to make full response to any inquiries in
      connection with this application for employment. If requested, I agree to
      submit to physical exam, background investigation, and/or screening for
      illegal substances upon conditional offer of employment. I understand that
      this form is NOT an offer of employment, and that an offer of employment,
      if tendered, does NOT constitute for continued guaranteed employment.
    </p>
  );

  const onFreshGradStatusSelect = (e: any) => {
    setIsFreGrad(e.target.value);
  };

  const uploadSingleFile = async (e: any) => {
    if (e.target.files[0]) {
      const targetFile = e.target.files[0];
      if (targetFile.type.includes("image/")) {
        if (targetFile.size > data.maximumImageSize) {
          return message.error("Image exceeds 2MB, please choose another one.");
        }
        await dispatchUploadFile(targetFile, targetFile.name);
      } else {
        message.error("Please select an image");
      }
    } else {
      message.error("Please select an image");
    }
  };

  const onSubmitPersonalProfile = async () => {
    try {
      const values: PersonalFormProps = await personalForm.validateFields();
      const {
        birthdate,
        date_completed,
        expected_salary,
        ...subValue
      } = values;

      const expectedSalaryFormatted = parseInt(String(expected_salary).replace(/,/g, "") as any, );

      const birthdateFormatted = Timestamp.fromDate(
        new Date(birthdate.toDate())
      );
      const dateCompletedFormatted = Timestamp.fromDate(
        new Date(date_completed.toDate())
      );

      const isInvalidReferredBy = validation.invalidReferredBy.includes(
        subValue.referred_by?.toLowerCase().replace(/\s/g, "")
      );

      const validFullNameLength =
        subValue.first_name.length +
          (subValue?.middle_name || "").length +
          subValue.last_name.length <=
        50;

      setApplicantInfo({
        ...applicantInfo,
        image: image,
        applicantName: {
          firstName: subValue.first_name,
          middleName: subValue.middle_name || "",
          lastName: subValue.last_name,
        },
        positionApplied: subValue.position_applied,
        expectedSalary: expectedSalaryFormatted,
        birthDate: birthdateFormatted,
        civilStatus: subValue.civil_status,
        degree: subValue.degree,
        dateCompleted: dateCompletedFormatted,
        school: subValue.school,
        portfolio: subValue.portfolio || '',
        course: subValue.course,
        referredBy: subValue.referred_by
          ? isInvalidReferredBy
            ? ""
            : values.referred_by
          : applicantInfo.referredBy,
      });

      validFullNameLength && next();
    } catch (errorInfo: any) {
      const firstError = errorInfo?.errorFields[0].name[0];
      personalForm.scrollToField(firstError, { behavior: "smooth" });
    }
  };

  const onSubmitSkills = async () => {
    try {
      const values: SkillsFormProps = await skillsForm.validateFields();

      setApplicantInfo({
        ...applicantInfo,
        // @ts-ignore
        skills: values ? values : [],
      });
      next();
    } catch (errorInfo: any) {
      const firstError = errorInfo?.errorFields[0].name[0];
      skillsForm.scrollToField(firstError, { behavior: "smooth" });
    }
  };

  const onSubmitContacts = async () => {
    try {
      const values: ContactsFormProps = await contactsForm.validateFields();

      let characterRef = Object.keys(values)
        // eslint-disable-next-line array-callback-return
        .map((key: string) => {
          if (key.includes("character_reference")) {
            //@ts-ignore
            return values[key];
          }
        })
        .filter((x) => x !== undefined);

      setCurrent(3);

      setApplicantInfo({
        ...applicantInfo,
        linkedinUrl: values.linkedin_url
          ? values.linkedin_url
          : applicantInfo.linkedinUrl,
        email: values.email,
        contactNumber: values.contact_number,
        contactTimeTo: values.contact_time_to.toString(),
        contactTimeFrom: values.contact_time_from.toString(),
        address: values.address,
        characterReference: characterRef,
      });
    } catch (errorInfo: any) {
      let firstError = errorInfo?.errorFields[0].name[0];
      if (/character_reference_/.test(firstError)) {
        firstError = errorInfo?.errorFields[0].name[1];
      }
      contactsForm.scrollToField(firstError, { behavior: "smooth" });
    }
  };

  const onSubmitPreScreening = async () => {
    try {
      preScreeningForm.submit();
      const values: PreScreeningFormProps = await preScreeningForm.validateFields();
      if (image) {
        await dispatchSetShowPrompt(false);
        await dispatchAddApplicant({
          ...applicantInfo,
          willingToRelocate: values.willing_to_relocate,
          yearsOfExperience: values.years_of_experience,
          recentWork: values.recent_work,
          reasonOfLeaving: values.reason_of_leaving,
          toolsUsed: values.tools_used,
          agileExperience: values.agile_experience,
          workOffice: values.work_office,
          platformApplied: values.platform_applied,
          resume: image,
        });

        // history.replace("/careers/success");
      } else {
        // message.error("Please upload your resume");
        setShowUploadResumeError(true);
      }
    } catch (errorInfo) {
      !image && setShowUploadResumeError(true);
    }
  };

  const onLeavePreScreeningForm = async () => {
    const recentWork: string = await preScreeningForm.getFieldValue(
      "recent_work"
    );
    const yearsOfExperience: string = await preScreeningForm.getFieldValue(
      "years_of_experience"
    );
    const toolsUsed: string = await preScreeningForm.getFieldValue(
      "tools_used"
    );
    return setApplicantInfo({
      ...applicantInfo,
      yearsOfExperience: yearsOfExperience,
      recentWork: recentWork,
      toolsUsed: toolsUsed,
    });
  };

  const next = () => {
    const newCurrent = current + 1;
    return setCurrent(newCurrent);
  };

  const previous = () => {
    const newCurrent = current - 1;
    setCurrent(newCurrent);
  };

  const onSelectPosition = (selected: { position: string, skills: string[], key: string }) => {
    setSkillList(selected.skills);
  };

  const yearsOfExperienceItems: JSX.Element = (
    <>
      <Option value="No experience">No experience</Option>
      <Option value="Has background knowledge">Has background knowledge</Option>
      <Option value="Can do so with minimal guidance">
        Can do so with minimal guidance
      </Option>
      <Option value="Expert to do it">Expert to do it</Option>
      <Option value="Can teach others">Can teach others</Option>
    </>
  );

  const contactTimeOptionItems = [
    { value: "9:00 AM", disabled: false },
    { value: "10:00 AM", disabled: false },
    { value: "11:00 AM", disabled: false },
    { value: "12:00 PM", disabled: false },
    { value: "1:00 PM", disabled: false },
    { value: "2:00 PM", disabled: false },
    { value: "3:00 PM", disabled: false },
    { value: "4:00 PM", disabled: false },
    { value: "5:00 PM", disabled: false },
    { value: "6:00 PM", disabled: false },
  ];

  const steps: StepsProps = [
    {
      id: 0,
      title: "Personal",
      content: (
        <PersonalProfile
          formRef={personalForm}
          onFinish={onSubmitPersonalProfile}
          validateMessages={validation.validateMessages}
          image={image}
          defaultAvatar={avatar}
          uploadSingleFile={uploadSingleFile}
          onSelectPosition={onSelectPosition}
          positionAppliedOptionItems={
            <>
              {positions.map((pos: any) => (
                <Option key={pos.key} value={JSON.stringify(pos)}>
                  {pos.position}
                </Option>
              ))}
            </>
          }
          civilStatusOptionItems={
            <>
              {data.civilStatus.map((status: string) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </>
          }
          degreeOptionItems={
            <>
              {data.degrees.map((status: string) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </>
          }
          gutter={theme.gutter}
          textRules={validation.stringRules}
          commonRules={validation.commonRules}
          applicantInfo={applicantInfo}
          setApplicantInfo={setApplicantInfo}
        />
      ),
    },
    {
      id: 1,
      title: "Knowledge and Skills",
      content: (
        <SkillsForm
          skillList={skillList}
          onFinish={onSubmitSkills}
          formRef={skillsForm}
          yearsOfExperienceItems={yearsOfExperienceItems}
        />
      ),
    },
    {
      id: 2,
      title: "Contacts",
      content: (
        <ApplicantContacts
          isFreshGrad={isFreshGrad}
          onFreshGradStatusSelect={onFreshGradStatusSelect}
          onFinish={onSubmitContacts}
          formRef={contactsForm}
          contactTimeOptionItems={contactTimeOptionItems}
        />
      ),
    },
  ];

  return (
    <>
      <Prompt
        when={showPrompt}
        message="Are you sure you want to leave? Changes that you made may not be saved."
      />
      <ApplicantForm
        {...props}
        current={current}
        onSubmitPersonalProfile={onSubmitPersonalProfile}
        onSubmitSkills={onSubmitSkills}
        onSubmitContacts={onSubmitContacts}
        onSubmitPreScreening={onSubmitPreScreening}
        showUploadResumeError={showUploadResumeError}
        setShowUploadResumeError={setShowUploadResumeError}
        preScreeningForm={preScreeningForm}
        previous={previous}
        steps={steps}
        modalTitle={modalTitle}
        modalBody={modalBody}
        onLeavePreScreeningForm={onLeavePreScreeningForm}
        applicantInfo={applicantInfo}
      />
    </>
  );
};

export default ApplicantDetailsStepperPage;
